import React, { useId, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PageShell, usePageTitle } from "./site/Layout";
import { buttons } from "./site/ui";
import { CONSENT_KEY, PREFS_KEY, disableAnalytics, readPreferences } from "./CookieConsentOverlay";

function CookieSwitch({ title, description, checked, disabled = false, onChange }) {
  const id = useId();
  return (
    <div className="flex items-start justify-between gap-6 p-5">
      <div>
        <p id={`${id}-label`} className="font-semibold text-slate-900">
          {title}
        </p>
        <p id={`${id}-description`} className="mt-1 text-sm leading-6 text-slate-600">
          {description}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-3">
        {disabled && <span className="text-xs font-medium text-slate-500">Always on</span>}
        <button
          type="button"
          role="switch"
          aria-checked={checked}
          aria-labelledby={`${id}-label`}
          aria-describedby={`${id}-description`}
          disabled={disabled}
          onClick={onChange}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 ${
            checked ? "bg-blue-600" : "bg-slate-300"
          }`}
        >
          <span
            className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
              checked ? "translate-x-[1.375rem]" : "translate-x-0.5"
            }`}
          />
        </button>
      </div>
    </div>
  );
}

export default function CookiePreferences() {
  usePageTitle("Cookie preferences");
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(() => readPreferences().analytics);

  const save = () => {
    localStorage.setItem(PREFS_KEY, JSON.stringify({ essential: true, analytics }));
    localStorage.setItem(CONSENT_KEY, "custom");
    // Turning analytics off also deletes the Google Analytics cookies already set.
    if (!analytics) disableAnalytics();
    navigate("/");
    // Reload so switching analytics off takes effect straight away.
    window.location.reload();
  };

  const linkClass = "font-medium text-blue-700 underline underline-offset-2 hover:text-blue-800";

  return (
    <PageShell>
      <div className="mx-auto w-full max-w-3xl px-4 py-12 sm:px-6 sm:py-16">
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">Cookie preferences</h1>
        <p className="mt-4 text-lg leading-8 text-slate-600">
          Choose which cookies Car Sense can use. You can change this at any time from the link in the footer.
        </p>

        <div className="mt-8 divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white">
          <CookieSwitch
            title="Essential cookies"
            description="Needed for the site to work, for example to keep you signed in and to remember these choices."
            checked
            disabled
          />
          <CookieSwitch
            title="Analytics cookies"
            description="Google Analytics tells us which pages people visit and how they use them, so we can improve the site."
            checked={analytics}
            onChange={() => setAnalytics((value) => !value)}
          />
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <button type="button" onClick={save} className={buttons.primary}>
            Save preferences
          </button>
          <Link to="/" className={buttons.secondary}>
            Cancel
          </Link>
        </div>

        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">About Google Analytics</h2>
          <div className="mt-4 space-y-4 text-sm leading-6 text-slate-600">
            <p>If you allow analytics cookies, Google Analytics collects:</p>
            <ul className="list-disc space-y-1 pl-5">
              <li>An anonymised IP address and your general location (not your precise location)</li>
              <li>Your browser, device type and operating system</li>
              <li>The pages you visit, the page you came from and when you leave</li>
            </ul>
            <p>
              We don't send your name, email address or payment details to Google Analytics. Google may use this data as
              described in{" "}
              <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer" className={linkClass}>
                how Google uses information from sites that use its services
              </a>
              . You can also opt out on every site with the{" "}
              <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className={linkClass}>
                Google Analytics opt-out browser add-on
              </a>
              .
            </p>
          </div>
        </section>
      </div>
    </PageShell>
  );
}
